import React, { ReactNode, useState } from 'react'

import { Steps } from 'antd'
import classNames from 'classnames'

import { WizardStep, StepStatus as WizardStepStatus } from 'core/domain/wizard/wizard'

import { useBreakPoints } from 'common/hooks'

import { NavItemProps } from './models'

const { Step } = Steps
const DEFAULT_NAVIGATION_ITEM_INDEX = 0

declare type StepStatus = 'finish' | 'process' | 'wait'

interface HeaderNavigationProps {
  navItemsMapping: NavItemProps[]
  navigationItems: WizardStep[]
  hasDirtyData: boolean
  setCurrentNavigationItem: (item: NavItemProps) => void
  bindGoBack: (goBack: () => void) => void
  bindGoNext: (goNext: () => void) => void
  bindGetIndexOfActiveNavigationItem: (getIndexOfActiveNavigationItem: (items: WizardStep[]) => number) => void
  bindSetEditingNavigationItem: (setEditingNavigationItem: (item: string | undefined) => void) => void
  showDisclaimerModal: (onCancel: () => void, translate: any) => void
  translate: any
}

export const HeaderNavigation: React.FC<HeaderNavigationProps> = (props) => {
  const { isXl } = useBreakPoints()

  const { bindGoBack, bindGoNext, bindGetIndexOfActiveNavigationItem, bindSetEditingNavigationItem, translate } = props
  const [editingNavigationItem, setEditingNavigationItem] = useState<string | undefined>(undefined)

  const navigationItemsGenerator = (items: WizardStep[]): ReactNode => {
    const activeNavigationItem = getIndexOfActiveNavigationItem(items)
    return (
      <Steps current={activeNavigationItem} type="navigation" direction={isXl ? 'horizontal' : 'vertical'} size="small">
        {items.map((item, index) => {
          const categoryItemStatus = getNavigationItemStatus(item)
          return (
            <Step
              title={getTitleContentOfNavigationItem(item)}
              key={index}
              status={categoryItemStatus}
              className={classNames({
                steps__item: true,
                steps__item_active: activeNavigationItem === index,
                steps__item_complete: categoryItemStatus === 'finish',
                'ant-steps-item-active': categoryItemStatus === 'finish',
              })}
              disabled={categoryItemStatus === 'wait'}
            />
          )
        })}
      </Steps>
    )
  }

  const getIndexOfActiveNavigationItem = (items: WizardStep[]): number => {
    try {
      if (editingNavigationItem) {
        const navItemIndex = items.findIndex((item) => item.id === editingNavigationItem)
        if (navItemIndex === -1) throw new Error('Navigation Item not found')
        const navItem = items[navItemIndex]
        if (navItem.status === WizardStepStatus.Active || navItem.status === WizardStepStatus.Complete)
          return navItemIndex
        else setEditingNavigationItem(undefined)
      }

      const activeNavigationItemIndex = items.findIndex((item) => item.status === WizardStepStatus.Active)

      if (activeNavigationItemIndex === -1) return DEFAULT_NAVIGATION_ITEM_INDEX
      return activeNavigationItemIndex
    } catch (e) {
      console.log(items)
      console.log(e)
      return DEFAULT_NAVIGATION_ITEM_INDEX
    }
  }

  const getNavigationItemStatus = (navigationItem: WizardStep): StepStatus => {
    if (editingNavigationItem === navigationItem.id) {
      return 'process'
    } else if (editingNavigationItem && navigationItem.status === WizardStepStatus.Active) {
      return 'wait'
    } else if (navigationItem.status === WizardStepStatus.Complete) {
      return 'finish'
    } else if (navigationItem.status === WizardStepStatus.Active) {
      return 'process'
    } else {
      return 'wait'
    }
  }

  const getTitleContentOfNavigationItem = (navigationItem: WizardStep): ReactNode => {
    return navigationItem.status === WizardStepStatus.Active || navigationItem.status === WizardStepStatus.Complete ? (
      <h5
        className="pointer mb-0"
        onClick={() => {
          if (props.hasDirtyData) {
            props.showDisclaimerModal(() => {
              redirectToNavigationItem(navigationItem.id)
            }, translate)
          } else {
            redirectToNavigationItem(navigationItem.id)
          }
        }}
      >
        {navigationItem.name}
      </h5>
    ) : (
      <h5 className="mb-0">{navigationItem.name}</h5>
    )
  }

  const redirectToNavigationItem = (id: string) => {
    try {
      const navigationItemProps = props.navItemsMapping.find((navItem) => navItem.key === id)
      if (navigationItemProps === undefined)
        throw new Error(translate('admin-dashboard.benefit-details.class-design.errors.navigation-item-not-found'))
      props.setCurrentNavigationItem(navigationItemProps)
      setEditingNavigationItem(id)
    } catch (e) {
      console.log(id)
      console.log(e)
    }
  }

  const goBack = () => {
    try {
      const currentNavigationItemIndex = getIndexOfActiveNavigationItem(props.navigationItems)
      const item = props.navigationItems[currentNavigationItemIndex]
      if (item === undefined)
        throw new Error(translate('admin-dashboard.benefit-details.class-design.errors.navigation-item-not-found'))

      if (currentNavigationItemIndex > 0) {
        let nextItem = props.navigationItems[currentNavigationItemIndex - 1]
        redirectToNavigationItem(nextItem.id)
      } else {
        redirectToNavigationItem(props.navigationItems[DEFAULT_NAVIGATION_ITEM_INDEX].id)
      }
    } catch (e) {
      console.log(e)
      redirectToNavigationItem(props.navigationItems[DEFAULT_NAVIGATION_ITEM_INDEX].id)
    }
  }

  const goNext = () => {
    try {
      const currentNavigationItemIndex = getIndexOfActiveNavigationItem(props.navigationItems)
      const item = props.navigationItems[currentNavigationItemIndex]
      if (item === undefined)
        throw new Error(translate('admin-dashboard.benefit-details.class-design.errors.navigation-item-not-found'))
      item.status = WizardStepStatus.Complete
      if (currentNavigationItemIndex < props.navigationItems.length - 1) {
        let nextItem = props.navigationItems[currentNavigationItemIndex + 1]
        if (nextItem.status === WizardStepStatus.Disabled) {
          nextItem.status = WizardStepStatus.Active
        }
        redirectToNavigationItem(nextItem.id)
      } else {
        redirectToNavigationItem(props.navigationItems[DEFAULT_NAVIGATION_ITEM_INDEX].id)
      }
    } catch (e) {
      console.log(e)
      redirectToNavigationItem(props.navigationItems[DEFAULT_NAVIGATION_ITEM_INDEX].id)
    }
  }

  bindGetIndexOfActiveNavigationItem(getIndexOfActiveNavigationItem)
  bindSetEditingNavigationItem(setEditingNavigationItem)
  bindGoNext(goNext)
  bindGoBack(goBack)

  return <>{navigationItemsGenerator(props.navigationItems)}</>
}
